import { createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { BalanceTarget, BalanceValue } from '@mona/models';
import { EncountersActions } from '@mona/pdms/data-access-encounters';
import { TerminologyActions } from '@mona/pdms/data-access-terminology';
import { EntriesInterval } from '@mona/shared/date';
import { BalanceTargetState, BalanceValueState } from '../../entities';
import { BalanceActions, BalanceTargetsActions, BalanceValuesActions } from '../actions';

export const balanceValuesAdapter = createEntityAdapter<BalanceValue>({
    selectId: (item: BalanceValue) => item.date,
});

export const initialBalanceValueState: BalanceValueState = balanceValuesAdapter.getInitialState({
    cachedBalanceValues: {} as Record<EntriesInterval, BalanceValue[]>,
});

export const balanceTargetsAdapter = createEntityAdapter<BalanceTarget>({
    selectId: (item: BalanceTarget) => new Date(item.date).toISOString(),
});

export const initialBalanceTargetState: BalanceTargetState = balanceTargetsAdapter.getInitialState({
    error: null,
    isLoading: false,
});

export const balanceValuesReducer = createReducer(
    initialBalanceValueState,

    on(BalanceValuesActions.upsertBalanceValues, (state, action) =>
        balanceValuesAdapter.upsertMany(action.balanceValues, state),
    ),

    on(BalanceValuesActions.loadBalanceValuesFailure, state => {
        return balanceValuesAdapter.removeAll(state);
    }),

    on(BalanceValuesActions.loadBalanceValuesSuccess, (state, action) => {
        const { balanceValues: results } = action;
        return balanceValuesAdapter.setAll(results, {
            ...state,
            cachedBalanceValues: {
                ...state.cachedBalanceValues,
                [action.interval]: results,
            },
        });
    }),

    on(BalanceValuesActions.clearBalanceValuesCache, EncountersActions.clearEncounter, state => ({
        ...state,
        cachedBalanceValues: {} as any,
    })),

    on(BalanceValuesActions.clearBalanceValues, EncountersActions.clearEncounter, state => {
        return balanceValuesAdapter.removeAll(state);
    }),
);

export const balanceTargetReducer = createReducer(
    initialBalanceTargetState,

    on(BalanceTargetsActions.loadBalanceTargetSuccess, (state, { values }) => {
        return balanceTargetsAdapter.setAll(values, {
            ...state,
        });
    }),

    on(BalanceTargetsActions.loadBalanceTargetFailure, state => ({
        ...state,
    })),

    on(BalanceTargetsActions.upsertBalanceTargets, (state, { values }) => {
        return balanceTargetsAdapter.upsertMany(values, state);
    }),

    on(BalanceTargetsActions.removeBalanceTargets, (state, { ids }) => {
        // BalanceTargets are keyed in the adapter by their `date` ISO string (see the
        // custom `selectId` above), but callers identify reverted optimistic creates by
        // changelog `modelId` — i.e. the BalanceTarget `id` (a UUID). Passing those UUIDs
        // straight to `removeMany` matched nothing, so the phantom row lingered stuck in
        // `isSaving: true`. Translate the incoming model ids to the adapter's date keys
        // (also tolerating callers that already pass a date key) before removing.
        const idSet = new Set(ids);
        const keysToRemove = (state.ids as string[]).filter(
            key => idSet.has(key) || idSet.has(state.entities[key]?.id as string),
        );
        return balanceTargetsAdapter.removeMany(keysToRemove, state);
    }),
);

/**
 * Balance loading reducer - ALL nested states
 */
export const balanceLoadingReducer = createReducer<boolean, Action>(
    true,
    on(BalanceActions.setIsLoadingAction, (state, { isLoading }) => isLoading),
    on(
        BalanceValuesActions.loadBalanceValues,
        //
        BalanceActions.loadOutputsAction.action,
        BalanceActions.loadMedicationAdministrations,
        TerminologyActions.loadMedicationsAction.action,
        () => true,
    ),
    on(
        BalanceValuesActions.loadBalanceValuesSuccess,
        BalanceValuesActions.loadBalanceValuesFailure,
        BalanceValuesActions.clearBalanceValues,
        EncountersActions.clearEncounter,
        //
        BalanceActions.loadOutputsAction.succeededAction,
        BalanceActions.loadMedicationAdministrationsSuccess,
        BalanceActions.loadMedicationAdministrationsFailed,
        BalanceActions.loadMedicationAdministrationsCancelled,
        BalanceActions.loadOutputsAction.failedAction,
        BalanceActions.loadOutputsAction.clearAction,
        TerminologyActions.loadMedicationsAction.succeededAction,
        TerminologyActions.loadMedicationsAction.failedAction,
        () => false,
    ),
);
